// The Overwolf uninstall survey (/ow-survey).
//
// Opened in the user's default browser by the Overwolf uninstaller (uninstall.html
// in the sh-overwolf repo) at the moment the app is deleted. Consequences that
// shape this component:
//
// - It renders OUTSIDE the App shell (a top-level route in app/router.tsx, sibling
//   of '/'), so there is no NavBar, no store bootstrap, no session, no toasts. The
//   user's app no longer exists; nothing here may depend on it.
// - Because AppLoader is not mounted, this route must remove the static
//   #app-preloader from index.html itself — otherwise the preloader covers the
//   page forever (see the react-basename-bare-path-preloader-hang note).
// - The e-mail arrives as a URL param and only when someone was logged in. Both
//   states are designed, not one-designed-one-degraded: with an e-mail we say who
//   is answering, without one we say the answer is anonymous. It is unverified
//   input (anyone can edit a URL) — it is echoed to the server and never trusted.

import { useEffect, useMemo, useState } from 'react';
import SonghuntLogo from '../../shared/components/SonghuntLogo';
import { assetUrl } from '../../shared/asset-url';
import { APP_VERSION, logger } from '../../shared/logging/logger';
import {
  COMMENT_MAX_LENGTH,
  SURVEY_REASONS,
  submitSurvey,
  type SurveyReasonKey,
} from '../../api/ow-survey';
import './OwSurvey.scss';

type Status = 'idle' | 'sending' | 'sent' | 'error';

/** The uninstaller omits `email` entirely when nobody was logged in — an empty or
 *  malformed value is treated the same as absent rather than sent on as junk. */
function readEmail(params: URLSearchParams): string | undefined {
  const raw = params.get('email')?.trim();
  if (!raw || !raw.includes('@')) return undefined;
  return raw;
}

export default function OwSurvey() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const email = useMemo(() => readEmail(params), [params]);
  // `v` is the app version at uninstall time. Fall back to this build's version so
  // a response is never version-less.
  const appVersion = params.get('v')?.trim() || APP_VERSION;

  const [reason, setReason] = useState<SurveyReasonKey | null>(null);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  // This route is outside the App shell, so AppLoader never runs — take the
  // preloader down here or it hides the page permanently.
  useEffect(() => {
    document.getElementById('app-preloader')?.remove();
    document.title = 'Songhunt - Uninstall feedback';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reason || status === 'sending') return;

    setStatus('sending');
    try {
      await submitSurvey({
        reason,
        reasonLabel: SURVEY_REASONS.find(r => r.key === reason)!.label,
        comment: comment.trim().slice(0, COMMENT_MAX_LENGTH),
        ...(email ? { email } : {}),
        appVersion,
        source: 'ow-uninstall',
        submittedAt: new Date().toISOString(),
      });
      setStatus('sent');
    } catch (err) {
      logger.error('app', 'ow-survey submit failed', { err: String(err) });
      setStatus('error');
    }
  };

  return (
    <div className="ow-survey">
      <div className="ow-survey__grid">
        <section className="ow-survey__brand">
          <div className="ow-survey__brand-inner">
            <div className="ow-survey__lockup">
              <SonghuntLogo className="ow-survey__sh-logo" />
              <span className="ow-survey__plus" aria-hidden="true">
                +
              </span>
              <img
                className="ow-survey__ow-logo"
                src={assetUrl('assets/logos/overwolf-logo.svg')}
                alt="Overwolf"
              />
            </div>

            <div>
              <p className="ow-survey__eyebrow">Uninstall feedback</p>
              <h1 className="ow-survey__title">Sorry to see you go.</h1>
              <p className="ow-survey__sub">
                One question, about twenty seconds. It goes straight to the people who build the
                app.
              </p>
            </div>

            <p className="ow-survey__quote">
              We read every one of these. It is the only way we find out what actually drove you
              off.
            </p>

            {email ? (
              <span className="ow-survey__who">
                <span className="ow-survey__avatar" aria-hidden="true">
                  {email[0]}
                </span>
                Answering as <b>{email}</b>
              </span>
            ) : (
              <span className="ow-survey__anon">
                You are answering anonymously - we do not know who you are.
              </span>
            )}
          </div>
        </section>

        <section className="ow-survey__form">
          {status === 'sent' ? (
            <div className="ow-survey__form-inner ow-survey__thanks">
              <span className="ow-survey__check" aria-hidden="true">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </span>
              <h2 className="ow-survey__thanks-title">
                Thanks - your feedback goes straight to the team.
              </h2>
              <p className="ow-survey__sub">
                You can close this tab. If you ever want Songhunt back, it is one click away in the
                Overwolf app store.
              </p>
            </div>
          ) : (
            <form className="ow-survey__form-inner" onSubmit={handleSubmit}>
              <fieldset className="ow-survey__options">
                <legend className="ow-survey__question">
                  What is the main reason you uninstalled Songhunt?{' '}
                  <span className="ow-survey__required" aria-hidden="true">
                    *
                  </span>
                </legend>

                {SURVEY_REASONS.map(r => (
                  <label className="ow-survey__option" key={r.key}>
                    <input
                      type="radio"
                      name="reason"
                      value={r.key}
                      checked={reason === r.key}
                      onChange={() => setReason(r.key)}
                    />
                    <span className="ow-survey__ring" aria-hidden="true" />
                    {r.label}
                  </label>
                ))}
              </fieldset>

              <textarea
                className="ow-survey__comment"
                placeholder="Anything else you want to tell us? (optional)"
                maxLength={COMMENT_MAX_LENGTH}
                value={comment}
                onChange={e => setComment(e.target.value)}
                aria-label="Anything else you want to tell us?"
              />

              {status === 'error' && (
                <p className="ow-survey__error" role="alert">
                  Could not send - please try again.
                </p>
              )}

              <div className="ow-survey__actions">
                <button
                  className="ow-survey__submit"
                  type="submit"
                  disabled={!reason || status === 'sending'}
                >
                  {status === 'sending' ? 'Sending...' : 'Send feedback'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}
